import React, { useState, KeyboardEvent, useEffect } from 'react';
import { Button, Input } from '../../../../common/components';
import { i18n } from '../../../../common/services/i18n';
import {
    resetValidateProductVoucherAction,
    selectProductVoucherValidation, selectTransactionVouchers,
    validateProductVoucherAction
} from '../../ducks';
import { AppState } from '../../../../common/models/AppState';
import { connect } from 'react-redux';

import { TransactionVoucherResult } from '../../../../common/models/NewTransaction';
import { oc } from 'ts-optchain';
import { ModalSizes } from '../../../../common/constants/modal';
import { Icon, Modal } from 'antd';
import { addTransactionVoucherAction } from '../../ducks/cart';

interface DispatchToProps {
    setTransactionVouchers: (payload: string[]) => void;
    validateVoucher: (payload: string) => void;
    resetValidateVoucher: () => void;
}

interface StateToProps {
    result: TransactionVoucherResult | null;
    vouchers: string[];
}

type DiscountVoucherProps = {
    voucher?: string;
} & DispatchToProps & StateToProps;

const DiscountVoucher = ({
    voucher,
    setTransactionVouchers,
    validateVoucher,
    resetValidateVoucher,
    result,
    vouchers
}: DiscountVoucherProps) => {
    const [feedbackModal, setFeedbackModal] = useState(false);
    const [code, setCode] = useState(voucher || '');

    useEffect(() => {
        if (result && result.publicKey === code) {
            if (result.status !== 'loading') {
                setFeedbackModal(true);
            }
        }
    }, [result]);

    const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleVerifyClick();
        }
    };

    const handleVerifyClick = () => {
        if (vouchers.includes(code)) {
            return;
        }

        validateVoucher(code);
    };

    const handleConfirmClick = () => {
        setTransactionVouchers([...vouchers, code]);
        resetValidateVoucher();
        setFeedbackModal(false);
        setCode('');
    };

    const handleCloseClick = () => {
        setFeedbackModal(false);
        resetValidateVoucher();
        setCode('');
    };

    const handleRemoveVoucherClick = () => {
        setTransactionVouchers(vouchers.filter(v => v !== code));
    };

    return (
        <>
            <div className="discount-voucher__container">
                <Input
                    placeholder={i18n.t('informations.discountVoucher.placeholder')}
                    value={code}
                    onKeyDown={handleInputKeyDown}
                    onChange={(e) => setCode(e.currentTarget.value)}
                    readOnly={!!voucher}
                />
                {!voucher && (
                    <Button
                        onClick={handleVerifyClick}
                        size="small"
                        loading={oc(result).status() === 'loading'}
                        disabled={!code.length}
                        className="discount-voucher__submit-button"
                    >
                        {i18n.t('informations.discountVoucher.verify')}
                    </Button>
                )}
                {voucher && (
                    <button
                        className="discount-voucher__remove-button"
                        onClick={handleRemoveVoucherClick}
                    >
                        <Icon type="close-circle" />
                    </button>
                )}
            </div>
            {result && result.publicKey === code && feedbackModal && (
                <Modal
                    visible={true}
                    width={ModalSizes.small}
                    footer={null}
                    className="discount-voucher-modal"
                >
                    <div className="discount-voucher-modal__content">
                        <h3>{i18n.t('informations.discountVoucher.modal.title')}</h3>
                        {result.status === 'applied' && (
                            <>
                                <Icon
                                    type="check-circle"
                                    className="discount-voucher-modal__icon discount-voucher-modal--success"
                                />
                                <span className="discount-voucher-modal--success">
                                    {i18n.t('informations.discountVoucher.modal.success')}
                                </span>
                            </>
                        )}
                        {result.status === 'assigned' && (
                            <>
                                <Icon
                                    type="warning"
                                    className="discount-voucher-modal__icon discount-voucher-modal--warning"
                                />
                                <span className="discount-voucher-modal--warning">
                                    {i18n.t('informations.discountVoucher.modal.assigned')}
                                </span>
                            </>
                        )}
                        {result.error && (
                            <>
                                <Icon
                                    type="exclamation-circle"
                                    className="discount-voucher-modal__icon discount-voucher-modal--error"
                                />
                                <span className="discount-voucher-modal--error">
                                    {i18n.t(`informations.discountVoucher.modal.error.${result.error}`)}
                                </span>
                            </>
                        )}
                    </div>
                    <div className="discount-voucher-modal__footer">
                        {result.status === 'applied' ? (
                            <>
                                <Button onClick={handleConfirmClick}>
                                    {i18n.t('informations.discountVoucher.modal.confirm')}
                                </Button>
                                <Button type="primary" onClick={handleCloseClick}>
                                    {i18n.t('informations.discountVoucher.modal.cancel')}
                                </Button>
                            </>
                        ) : (
                            <Button type="primary" onClick={handleCloseClick}>
                                {i18n.t('informations.discountVoucher.modal.close')}
                            </Button>
                        )}
                    </div>
                </Modal>
            )}
        </>
    );
};

const mapDispatchToProps: DispatchToProps = {
    setTransactionVouchers: addTransactionVoucherAction,
    validateVoucher: validateProductVoucherAction.request,
    resetValidateVoucher: resetValidateProductVoucherAction,
};

const mapStateToProps = (state: AppState): StateToProps => ({
    result: selectProductVoucherValidation(state),
    vouchers: selectTransactionVouchers(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscountVoucher);
